import { apiFetch } from './apiFetch';

export const getUsersCompanies = () =>
  apiFetch({
    url: '/api/v2/admin/user_profile/companies',
    method: 'GET',
  });

export const getUsers = ({ queryKey }) => {
  const [, { active_users_page, unconfirmed_users_page, pending_invites_page, search, filter, count }] = queryKey;
  return apiFetch({
    url: '/api/v2/admin/users/users',
    method: 'POST',
    body: { active_users_page, unconfirmed_users_page, pending_invites_page, search, filter, count },
  });
};

export const getUserAudits = ({ queryKey }) => {
  const [, { id, startDate, endDate, query }] = queryKey;
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (query) params.append('query', query);

  return apiFetch({
    url: `/api/v2/admin/users/${id}/audits?${params.toString()}`,
    method: 'GET',
  });
};

export const getUserInvites = ({ queryKey }) => {
  const [, { page, search, filter }] = queryKey;
  return apiFetch({
    url: '/api/v2/admin/users/user_invites',
    method: 'POST',
    body: { page, search, filter },
  });
};

export const updateUser = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/update_user',
    method: 'POST',
    body: serializedData,
  });

export const bulkUpdateUsers = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/bulk_update_users',
    method: 'POST',
    body: serializedData,
  });

export const inviteUsers = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/invite_users',
    method: 'POST',
    body: serializedData,
  });

export const getInvitationData = () =>
  apiFetch({
    url: '/api/v2/admin/users/invitation_data',
  });

export const getCraInternalContacts = (q) => {
  const url = new URL('/api/v2/admin/users/cra_internal_contacts', window.location.origin);
  url.searchParams.set('q', q);
  return apiFetch({ url: url.toString(), method: 'GET' });
};

export const destroyInvites = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/destroy_invites',
    method: 'POST',
    body: serializedData,
  });

export const destroyUsers = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/destroy_users',
    method: 'POST',
    body: serializedData,
  });

export const destroyInvitationOrUsers = (serializedData, type) => {
  if (type === 'users') {
    return destroyUsers(serializedData);
  }
  if (type === 'invites') {
    return destroyInvites(serializedData);
  }
};

export const resendInvites = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/resend_invites',
    method: 'POST',
    body: serializedData,
  });

export const resendConfirmation = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/resend_confirmation',
    method: 'POST',
    body: serializedData,
  });

export async function createOfflineConversion(data) {
  return apiFetch({
    url: '/api/v2/admin/offline_conversions',
    method: 'POST',
    body: data,
  });
}

// Field Notes "Explore with ALEX" admin (ADM-1497).
// Step 1: store the note by publication date and get back the per-channel explore URLs.
export async function createFieldNote(data) {
  return apiFetch({
    url: '/api/v2/admin/field_notes',
    method: 'POST',
    body: { field_note: data },
  });
}

// Loads an already-saved note by its editorial date, so the admin can reword a
// published note's composer line without re-pasting the body (ADM-1844).
// Returns the note, or null when nothing is stored for that date.
export async function fetchFieldNoteByDate(publicationDate) {
  const data = await apiFetch({
    url: `/api/v2/admin/field_notes?publication_date=${encodeURIComponent(publicationDate)}`,
    method: 'GET',
  });
  return data?.field_notes?.[0] || null;
}

// Step 2: record the published WordPress permalink and/or revise the note's
// ALEX composer line (ADM-1844). Pass only the attributes you are changing.
export async function updateFieldNote(id, attributes) {
  return apiFetch({
    url: `/api/v2/admin/field_notes/${id}`,
    method: 'PATCH',
    body: { field_note: attributes },
  });
}

export function getBehaviors() {
  return apiFetch({
    url: '/api/v2/admin/behaviors/get_behaviors',
  });
}

export function getBehavior({ behaviorID }) {
  return apiFetch({
    url: `/api/v2/admin/behaviors/${behaviorID}.json`,
    method: 'GET',
  });
}

export async function updateBehavior({
  data: curriculum_behavior,
  behaviorID,
}) {
  return apiFetch({
    url: `/api/v2/admin/behaviors/${behaviorID}`,
    method: 'PATCH',
    body: {
      curriculum_behavior,
    },
  });
}

export function updateHabitOrder(habits) {
  return apiFetch({
    url: '/api/v2/admin/help_to_habits/update_order',
    method: 'POST',
    body: {
      help_to_habit: {
        reorder: habits,
      },
    },
  });
}

export async function getCourses() {
  return apiFetch({
    url: '/api/v2/admin/courses/get_courses',
  });
}

export async function createNew({ data, behaviorID }) {
  return apiFetch({
    url: '/api/v2/admin/help_to_habits/create_new',
    method: 'POST',
    body: {
      data,
      behavior_id: behaviorID,
    },
  });
}

export async function deleteHelpToHabit({ helpToHabitID }) {
  return apiFetch({
    url: '/api/v2/admin/help_to_habits/delete_help_to_habit',
    method: 'POST',
    body: {
      help_to_habit_id: helpToHabitID,
    },
  });
}

export async function editHelpToHabit({ data: help_to_habit, helpToHabitID }) {
  return apiFetch({
    url: `/api/v2/admin/help_to_habits/${helpToHabitID}`,
    method: 'PATCH',
    body: {
      help_to_habit,
    },
  });
}

export async function editHelpToHabitExtra({
  data: help_to_habit_extra,
  behaviorId,
  extraId,
}) {
  const method = extraId ? 'PATCH' : 'POST';
  let url = `/api/v2/admin/behaviors/${behaviorId}/help_to_habit_extras`;
  if (extraId) {
    url = `${url}/${extraId}`;
  }
  return apiFetch({
    url,
    method,
    body: {
      help_to_habit_extra,
    },
  });
}

export async function createTenant(data) {
  data.subdomain = data.slug;
  return apiFetch({
    url: '/api/v2/admin/tenants',
    method: 'POST',
    body: {
      ...data,
    },
  });
}

export async function editTenant(data) {
  data.subdomain = data.slug;
  delete data.slug;
  return apiFetch({
    url: `/api/v2/admin/tenants/${data.id}`,
    method: 'PUT',
    body: {
      ...data,
    },
  });
}

export async function getTenants() {
  return apiFetch({
    url: '/api/v2/admin/tenants',
    method: 'GET',
  });
}

export async function getTenantUserAdmins({ queryKey }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${queryKey[1]}/admins`,
    method: 'GET',
  });
}

export async function getTenantBySlug({ queryKey }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${queryKey[0]}`,
    method: 'GET',
  });
}

export async function createTenantUserAdmin({ data, slug }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/admin`,
    method: 'POST',
    body: {
      email: data.email,
      profile_attributes: {
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
      },
    },
  });
}

export async function editTenantUserAdmin({ data, slug }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/admin`,
    method: 'PUT',
    body: {
      email: data.email,
      profile_attributes: {
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
      },
    },
  });
}

export async function getTenantUsers({ queryKey }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${queryKey[1]}/users`,
    method: 'GET',
  });
}

export async function createTenantUser({ data, slug }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/user`,
    method: 'POST',
    body: {
      email: data.email,
      profile_attributes: {
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
      },
    },
  });
}

export async function editTenantUser({ data, slug }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/user`,
    method: 'PUT',
    body: {
      email: data.email,
      profile_attributes: {
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
      },
    },
  });
}

export async function createTenantUserBulk({ data, slug }) {
  const formData = new FormData();
  formData.append('csv_file', data.file);
  if (data.group) {
    formData.append('group', data.group);
  }

  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/user/bulk`,
    method: 'POST',
    body: formData,
    stringify: false,
    getHeaders: (headers) => {
      delete headers['Content-Type'];
      return headers;
    },
  });
}

export function getTenantGroups({ queryKey }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${queryKey[1]}/groups/available`,
    method: 'GET',
  });
}

export async function activateTenantGroup({ group, slug }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/group/${group.id}/activate`,
    method: 'POST',
  });
}

export async function enableTenantBehavior({ course, behavior, slug }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/${course}/${behavior}/enable`,
    method: 'POST',
  });
}

export async function disableTenantBehavior({ course, behavior, slug }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/${course}/${behavior}/disable`,
    method: 'POST',
  });
}

export async function sendUserEmail({ user, slug }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/user/${user}/send_email`,
    method: 'POST',
  });
}

export async function sendTenantUserInvites({ slug, data, emails }) {
  const expirationDate = new Date();
  if (data.expirationType === 'days') {
    expirationDate.setDate(expirationDate.getDate() + data.expirationLength);
  } else if (data.expirationType === 'years') {
    expirationDate.setDate(expirationDate.getDate() + data.expirationLength);
  }
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/user/invites`,
    method: 'POST',
    body: {
      emails,
      access_type: data.limitedAccess,
      expiration_type: data.fixedExpirationDate ? 'length' : 'date',
      expires_at: data.fixedExpirationDate
        ? data.expirationDate
        : expirationDate,
      skip_email: data.skipEmail,
    },
  });
}

export async function resendTenantUserInvites({ slug, emails }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${slug}/user/invites/resend`,
    method: 'POST',
    body: {
      emails,
    },
  });
}

export async function getTenantUserInvites({ queryKey }) {
  return apiFetch({
    url: `/api/v2/admin/tenants/slug/${queryKey[1]}/user/invites`,
    method: 'GET',
  });
}

//alex
export function sendMessage({ message, sessionId }) {
  return apiFetch({
    url: '/api/v2/alex/message',
    method: 'POST',
    body: {
      message,
      session_id: sessionId,
    },
  });
}

export function getConversations(offset) {
  return apiFetch({
    url: `/api/v2/alex/conversations?offset=${offset}`,
    method: 'GET',
  });
}

export function getConversation(id) {
  return apiFetch({
    url: `/api/v2/alex/conversations/${id}`,
    method: 'GET',
  });
}

export function editConversationTitle(id, title) {
  return apiFetch({
    url: `/api/v2/alex/${id}`,
    method: 'PUT',
    body: {
      title,
    },
  });
}

export function deleteConversation(id) {
  return apiFetch({
    url: `/api/v2/alex/${id}`,
    method: 'DELETE',
  });
}

export function sendMessageFeedback({ liked, feedback, message_id }) {
  return apiFetch({
    url: '/api/v2/conversation_message_feedbacks/toggle',
    method: 'POST',
    body: {
      liked,
      feedback,
      message_id,
    },
  });
}

export function deleteMessageFeedback({ message_id }) {
  return apiFetch({
    url: '/api/v2/conversation_message_feedbacks/delete',
    method: 'DELETE',
    body: {
      message_id,
    },
  });
}

export async function getReportAlexUsage({ queryKey }) {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(
    '/api/v2/admin/reports/alex-usage',
    window.location.origin
  );
  const params = {
    page,
    count,
    search,
    filter,
  };

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function getReportDailyAlexUsage({ queryKey }) {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(
    '/api/v2/admin/reports/daily-alex-usage',
    window.location.origin
  );
  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && {
        filter: JSON.stringify(filter),
      }),
  };

  url.search = new URLSearchParams(params).toString();

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getReportAlexMonthlyUsage({ queryKey }) {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(
    '/api/v2/admin/reports/alex-monthly-usage',
    window.location.origin
  );
  const params = {
    page,
    count,
    search,
    filter,
    // ...(search && { search }),
    // ...(filter &&
    //   Object.keys(filter).length > 0 && {
    //     filter: JSON.stringify(filter),
    //   }),
  };

  // url.search = new URLSearchParams(params).toString();

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function getReportInvites({ queryKey }) {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL('/api/v2/admin/reports/invites', window.location.origin);
  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && {
        filter: JSON.stringify(filter),
      }),
  };

  url.search = new URLSearchParams(params).toString();

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getOrders({ queryKey }) {
  const [, { page = 1, search, filter, count = 10, type = 'courses' }] =
    queryKey;

  const url = new URL('/api/v2/admin/orders', window.location.origin);
  const params = {
    page,
    count,
    type,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && {
        filter: JSON.stringify(filter),
      }),
  };

  url.search = new URLSearchParams(params).toString();

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getOrderDetails({ queryKey }) {
  const [, { id }] = queryKey;

  const url = new URL(`/api/v2/admin/orders/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getUpdateOrder({ id, body }) {
  const url = new URL(
    `/api/v2/admin/orders/${id}/gift`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}

export async function getOrderGiftDetails({ queryKey }) {
  const [, { id }] = queryKey;

  const url = new URL(
    `/api/v2/admin/orders/${id}/gift`,
    window.location.origin
  );

  url.search = new URLSearchParams().toString();

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getWebinars({ queryKey }) {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(`/api/v2/admin/webinars`, window.location.origin);

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && {
        filter: JSON.stringify(filter),
      }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getWebinar({ queryKey }) {
  const [, { id }] = queryKey;

  const url = new URL(`/api/v2/admin/webinars/${id}`, window.location.origin);

  url.search = new URLSearchParams().toString();

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createWebinar(webinarData) {
  const url = new URL(`/api/v2/admin/webinars`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify({ curriculum_webinar: webinarData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function updateWebinar({ id, webinarData }) {
  const url = new URL(`/api/v2/admin/webinars/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'PUT',
    body: JSON.stringify({ curriculum_webinar: webinarData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function deleteWebinar({ id }) {
  const url = new URL(`/api/v2/admin/webinars/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export async function getModules({ queryKey }) {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(`/api/v2/admin/courses`, window.location.origin);

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && {
        filter: JSON.stringify(filter),
      }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getModule({ queryKey }) {
  const [, { id }] = queryKey;

  const url = new URL(`/api/v2/admin/courses/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createModule(moduleData) {
  const url = new URL(`/api/v2/admin/courses`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify({ curriculum_course: moduleData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function updateModule({ id, moduleData }) {
  const url = new URL(`/api/v2/admin/courses/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'PUT',
    body: JSON.stringify({ curriculum_course: moduleData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function deleteModule({ id }) {
  const url = new URL(`/api/v2/admin/courses/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export const getCourseBehaviors = async (courseId) => {
  const response = await fetch(`/api/v2/admin/courses/${courseId}/behaviors`, {
    method: 'GET',
  });
  return response.json();
};

export const updateCourseBehaviors = async (courseId, behaviorIds) => {
  const response = await fetch(`/api/v2/admin/courses/${courseId}/behaviors`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ behavior_ids: behaviorIds }),
  });
  return response.json();
};

export async function getAllBehaviors({ queryKey }) {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(`/api/v2/admin/behaviors`, window.location.origin);

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getBehaviorDetails({ queryKey }) {
  const [, { id }] = queryKey;

  const url = new URL(`/api/v2/admin/behaviors/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createBehavior(behaviorData) {
  const url = new URL(`/api/v2/admin/behaviors`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify({ curriculum_behavior: behaviorData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function updateBehaviorDetails({ id, behaviorData }) {
  const url = new URL(`/api/v2/admin/behaviors/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'PATCH',
    body: JSON.stringify({ curriculum_behavior: behaviorData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function deleteBehavior({ id }) {
  const url = new URL(`/api/v2/admin/behaviors/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export async function getAllExercises({ queryKey }) {
  const [, { behaviorId, page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/exercises`,
    window.location.origin
  );

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getExerciseDetails({ queryKey }) {
  const [, { behaviorId, id }] = queryKey;

  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/exercises/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createExercise({ behaviorId, exerciseData }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/exercises`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify({ curriculum_exercise: exerciseData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function updateExerciseDetails({ behaviorId, id, exerciseData }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/exercises/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'PUT',
    body: JSON.stringify({ curriculum_exercise: exerciseData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function deleteExercise({ behaviorId, id }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/exercises/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export async function getAllQuestions({ queryKey }) {
  const [, { behaviorId, page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/questions`,
    window.location.origin
  );

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getQuestionDetails({ queryKey }) {
  const [, { behaviorId, id }] = queryKey;

  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/questions/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createQuestion({ behaviorId, questionData }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/questions`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify({ curriculum_question: questionData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function updateQuestionDetails({ behaviorId, id, questionData }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/questions/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'PUT',
    body: JSON.stringify({ curriculum_question: questionData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function deleteQuestion({ behaviorId, id }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/questions/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export async function getAllBehaviorMaps({ queryKey }) {
  const [, { behaviorId, page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/behavior_maps`,
    window.location.origin
  );

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getBehaviorMapDetails({ queryKey }) {
  const [, { behaviorId, id }] = queryKey;

  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/behavior_maps/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createBehaviorMap({ behaviorId, behaviorMapData }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/behavior_maps`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify({ curriculum_behavior_map: behaviorMapData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function updateBehaviorMapDetails({
  behaviorId,
  id,
  behaviorMapData,
}) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/behavior_maps/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'PUT',
    body: JSON.stringify({ curriculum_behavior_map: behaviorMapData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function deleteBehaviorMap({ behaviorId, id }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/behavior_maps/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export async function getAllExamples({ queryKey }) {
  const [, { behaviorId, page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/examples`,
    window.location.origin
  );

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function getExampleDetails({ queryKey }) {
  const [, { behaviorId, id }] = queryKey;

  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/examples/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createExample({ behaviorId, exampleData }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/examples`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'POST',
    body: JSON.stringify({ curriculum_example: exampleData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function updateExampleDetails({ behaviorId, id, exampleData }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/examples/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'PUT',
    body: JSON.stringify({ curriculum_example: exampleData }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export async function deleteExample({ behaviorId, id }) {
  const url = new URL(
    `/api/v2/admin/behaviors/${behaviorId}/examples/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export async function getAllCohorts({ queryKey }) {
  const [, { page = 1, search, filter, count = 10 }] = queryKey;

  const url = new URL(`/api/v2/admin/cohorts`, window.location.origin);

  const params = {
    page,
    count,
    ...(search && { search }),
    ...(filter &&
      Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createCohort(data) {
  return apiFetch({
    url: '/api/v2/admin/cohorts',
    method: 'POST',
    body: {
      ...data,
    },
  });
}

export async function updateCohort(data) {
  return apiFetch({
    url: `/api/v2/admin/cohorts/update`,
    method: 'PUT',
    body: {
      ...data,
    },
  });
}

export async function getCohorts() {
  return apiFetch({
    url: '/api/v2/admin/cohorts',
    method: 'GET',
  });
}

export async function getCohortDetails({ queryKey }) {
  const [, { id }] = queryKey;

  const url = new URL(`/api/v2/admin/cohorts/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function deleteCohort({ id }) {
  const url = new URL(
    `/api/v2/admin/cohorts/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export async function addParticipantToCohort({ id, cohort }) {
  return apiFetch({
    url: `/api/v2/admin/cohorts/${id}/add_participant`,
    method: 'POST',
    body: { cohort },
  });
}

export async function addRepresentativesToCohort({ id, cohort }) {
  return apiFetch({
    url: `/api/v2/admin/cohorts/${id}/add_representatives`,
    method: 'POST',
    body: { cohort },
  });
}

export async function removeParticipantsFromCohort({ id, cohort, filter }) {
  const params = new URLSearchParams();
  if (cohort && cohort.userEmails && cohort.userEmails.length === 0) {
    params.append('cohort_id', id);
    if (filter) {
      params.append('filter', JSON.stringify(filter));
    }
  }
  const queryString = params.toString() ? `?${params.toString()}` : '';

  return apiFetch({
    url: `/api/v2/admin/cohorts/${id}/remove_participants${queryString}`,
    method: 'POST',
    body: { cohort },
  });
}

export async function removeRepresentativesFromCohort({ id, cohort }) {
  return apiFetch({
    url: `/api/v2/admin/cohorts/${id}/remove_representatives`,
    method: 'POST',
    body: { cohort },
  });
}

export const userExistEmail = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/user_exist_email',
    method: 'POST',
    body: serializedData,
  });

export const getEmailSuggestions = (serializedData) =>
  apiFetch({
    url: '/api/v2/admin/users/user_email_suggestion',
    method: 'POST',
    body: serializedData,
  });

export const suggestCohortEmails = ({ cohortId, query, filter = {} }) =>
  apiFetch({
    url: `/api/v2/admin/cohorts/${cohortId}/suggest_cohort_emails`,
    method: 'POST',
    body: {
      query,
      filter: JSON.stringify(filter || {}),
    },
  });

export const getCohortUsers = ({ queryKey }) => {
  const [, { id, page, filter, search }] = queryKey;
  return apiFetch({
    url: `/api/v2/admin/cohorts/${id}/get_cohort_users`,
    method: 'POST',
    body: {
      page,
      count: 10,
      search: search ?? '',
      filter: JSON.stringify(filter || {}),
    },
  });
};

export async function getAllCompanies({ queryKey }) {
  const [, { page = 1, search, count = 10 }] = queryKey;

  const url = new URL(`/api/v2/admin/companies`, window.location.origin);

  const params = {
    page,
    count,
    ...(search && { search }),
  };

  url.search = new URLSearchParams(params).toString();
  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function createCompany(data) {
  return apiFetch({
    url: '/api/v2/admin/companies',
    method: 'POST',
    body: {
      company: data,
    },
  });
}

export async function updateCompany({ id, data }) {
  return apiFetch({
    url: `/api/v2/admin/companies/${id}`,
    method: 'PUT',
    body: {
      company: data,
    },
  });
}

export async function getCompanyDetails({ queryKey }) {
  const [, { id }] = queryKey;

  const url = new URL(`/api/v2/admin/companies/${id}`, window.location.origin);

  return apiFetch({
    url: url.toString(),
    method: 'GET',
  });
}

export async function deleteCompany({ id }) {
  const url = new URL(
    `/api/v2/admin/companies/${id}`,
    window.location.origin
  );

  return apiFetch({
    url: url.toString(),
    method: 'DELETE',
  });
}

export async function getCompaniesMetadata() {
  return apiFetch({
    url: '/api/v2/admin/companies/metadata',
    method: 'GET',
  });
}
